import { Card, Col, Flex, Tag, Typography } from 'antd'
import { ArrowUpRight, Calendar, Users } from 'lucide-react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { type OrganizationLong } from 'src/api/main'
import { AvatarWithInitials, DateTimeCell } from 'src/components'
import { paths } from 'src/router'
import { normalizeWorkspaceAccessRole } from 'src/utils/workspaceAuthorization'

import { useStyle } from './styles'

type Props = {
  item: OrganizationLong
}

const ROLE_TAG_COLOR: Record<string, string> = {
  owner: 'gold',
  admin: 'geekblue',
  employee: 'default'
}

export const OrganizationItem = ({ item }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { styles, cx } = useStyle()

  const normalizedRole = normalizeWorkspaceAccessRole(item?.access_role)
  const roleLabel = normalizedRole
    ? t(`roles.${normalizedRole}`, { defaultValue: normalizedRole })
    : null
  const roleTagColor = normalizedRole
    ? (ROLE_TAG_COLOR[normalizedRole] ?? 'default')
    : 'default'

  const fullDomain = item?.domain_name
    ? `${item.domain_name}.${import.meta.env.VITE_APP_DOMAIN_URL}`
    : null

  // Always into the workspace shell, whatever the tier — the same landing the
  // switcher and post-login redirect use. What a member may actually open is
  // decided by their permissions once inside (`WorkspaceAccessRoute`), not by
  // the session's coarse `access_role`.
  const onActivate = () => navigate(paths.workspaces.home(String(item?.id)))

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onActivate()
    }
  }

  const memberCount = item?.users ?? 0

  return (
    <Col xs={24} sm={12} md={12} lg={8} xl={6}>
      <Card
        variant="outlined"
        className={styles.workspaceCard}
        styles={{ body: { padding: 16, height: '100%' } }}
        onClick={onActivate}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="button"
        aria-label={item?.name ?? ''}
      >
        <Flex vertical gap={14} className={styles.content}>
          <Flex align="flex-start" justify="space-between" gap={10}>
            <Flex align="flex-start" gap={12} flex={1} style={{ minWidth: 0 }}>
              {/* Name only, no id — one colour per workspace name everywhere
                  (sidebar tile, overview hero, onboarding preview). */}
              <AvatarWithInitials
                name={item?.name ?? ''}
                size={40}
                shape="square"
                src={item?.photo?.file}
              />

              <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
                <Typography.Text
                  ellipsis={{ tooltip: item?.name }}
                  className={styles.workspaceName}
                >
                  {item?.name}
                </Typography.Text>
                {fullDomain ? (
                  <Typography.Text
                    ellipsis={{ tooltip: fullDomain }}
                    className={styles.workspaceDomain}
                  >
                    {fullDomain}
                  </Typography.Text>
                ) : null}
              </Flex>
            </Flex>

            {roleLabel ? (
              <Tag color={roleTagColor} className={styles.roleTag}>
                {roleLabel}
              </Tag>
            ) : null}
          </Flex>

          <div className={styles.divider} />

          <Flex
            align="center"
            justify="space-between"
            gap={8}
            className={styles.footer}
          >
            <Flex align="center" gap={10} wrap className={styles.meta}>
              <Flex align="center" gap={5}>
                <Calendar size={13} className={styles.metaIcon} />
                <DateTimeCell
                  value={item?.date_created}
                  variant="date"
                  className={styles.metaText}
                />
              </Flex>
              <span className={styles.metaDot} aria-hidden />
              <Flex align="center" gap={5}>
                <Users size={13} className={styles.metaIcon} />
                <Typography.Text className={styles.metaText}>
                  {t('workspaces.membersCount', { count: memberCount })}
                </Typography.Text>
              </Flex>
            </Flex>
            <ArrowUpRight
              size={15}
              className={cx(styles.openIcon, 'ws-arrow')}
            />
          </Flex>
        </Flex>
      </Card>
    </Col>
  )
}
